import {
  NativeFieldExtra,
  PartialSalesforceFieldMappingLocalData,
  SalesforceFieldMappingLocalData,
  SalesforceResourceMappingLocalData,
} from 'module/api/integrations/salesforce/types';
import { SalesforceSyncTypes } from 'module/api/integrations/salesforce/types';
import { useTranslate } from 'module/hooks/use-translation';
import { Popconfirm } from 'module/ui/components/atoms/popconfirm';
import { CustomOption, ISingleValue } from 'module/ui/components/atoms/select';
import { faCircleMinus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useSalesForceMappableNativeResourceFields } from 'queries/salesforce';
import { useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import Select from 'react-select';
import { tv } from 'tailwind-variants';
import {
  getValidSyncOptions,
  PopoverSelect,
  SelectedSyncValue,
  SyncOptionLabel,
  SyncSelectStyles,
} from './commons';
import { syncOptions } from './use-field-mapping-actions';

const fieldMappingVariants = tv({
  slots: {
    container:
      'flex w-full items-center border-b border-[#f4f4f4] h-[40px] py-0.5',
    mainCell: 'flex items-center gap-2 px-2',
    syncCell: 'flex items-center gap-2 px-0',
    deleteButton:
      'flex items-center justify-center w-4 h-4 text-[#1d1d1f] hover:text-red-600 cursor-pointer transition-colors',
  },
});

type FieldMappingType = {
  integrationId: number;
  salesforceFieldValue: string;
  fieldMap: SalesforceFieldMappingLocalData;
  externalFields: CustomOption[];
  onRemove: () => void;
  onChange: (
    resourceId: number,
    mappingId: string,
    payload: PartialSalesforceFieldMappingLocalData,
  ) => void;
  fieldObject: SalesforceResourceMappingLocalData;
};

export const FieldMapping = ({
  integrationId,
  salesforceFieldValue,
  fieldObject,
  fieldMap,
  externalFields,
  onRemove,
  onChange,
}: FieldMappingType) => {
  const isCreatingMapping = useMemo(() => {
    return Boolean(
      fieldMap.id &&
        fieldMap.salesforceField &&
        fieldMap.rocketlaneField &&
        fieldMap.syncField &&
        fieldMap.fieldMappingConfigId == null,
    );
  }, [fieldMap]);

  const [getFields, setGetFields] = useState(false);
  const translate = useTranslate();

  const handleSyncOptionChange = (selected: ISingleValue) => {
    onChange?.(fieldObject.resourceMappingConfigId, fieldMap.id, {
      ...fieldMap,
      syncField: (selected?.value as SalesforceSyncTypes) ?? undefined,
    });
  };

  const {
    data: accountAndProjectData,
    isLoading: _isLoading,
    isInitialLoading,
  } = useSalesForceMappableNativeResourceFields(
    integrationId,
    fieldObject.nativeResourceType, // ✅ Use dynamic nativeResourceType (user/company/project)
    fieldObject.externalResourceType,
    salesforceFieldValue,
    fieldMap.fieldMappingConfigId ?? undefined, // ✅ Only pass if exists (editing mode)
    {
      enabled:
        !!fieldObject.externalResourceType &&
        !!salesforceFieldValue &&
        getFields,
    },
  );
  const isLoading = useMemo(() => {
    return !!fieldObject.externalResourceType &&
      !!salesforceFieldValue &&
      getFields
      ? _isLoading
      : isInitialLoading;
  }, [
    _isLoading,
    fieldObject.externalResourceType,
    getFields,
    isInitialLoading,
    salesforceFieldValue,
  ]);

  const handleNativeFieldChange = (value?: ISingleValue<NativeFieldExtra>) => {
    onChange?.(fieldObject.resourceMappingConfigId, fieldMap.id, {
      ...fieldMap,
      rocketlaneField: value ?? undefined,
    });
  };

  const handleExternalFieldChange = (selectedValue?: ISingleValue<boolean>) => {
    let temp: any;

    if (selectedValue) {
      temp = {
        ...selectedValue,
        label:
          selectedValue?.parent === fieldObject.externalResourceType
            ? selectedValue?.label
            : `[${selectedValue?.parent}] ${selectedValue?.label}`,
      };
    }
    onChange?.(fieldObject.resourceMappingConfigId, fieldMap.id, {
      ...fieldMap,
      salesforceField: temp,
      rocketlaneField: undefined,
      syncField: SalesforceSyncTypes.SyncExternalToNative,
    });
  };

  const projectFieldsText = translate('project_fields');
  const calculatedFieldsText = translate('calculated_fields');
  const companyFieldsText = translate('company_fields');
  const userFieldsText = 'Contact Fields'; // User/Contact fields

  const projectNativeFieldAsOptions = useMemo(
    () =>
      (accountAndProjectData ?? [])
        .filter(
          (data) =>
            data?.parentFieldId === 'project' ||
            data?.parentFieldId === null ||
            data?.parentFieldId === undefined,
        )
        .map(
          (field) =>
            ({
              value: field.integrationFieldId,
              label: field.fieldLabel ?? field.fieldName,
              parent: field?.parentFieldName
                ? field.parentFieldName === 'meta_data'
                  ? projectFieldsText
                  : field.parentFieldName
                : calculatedFieldsText,
              extra: {
                isEditable: field.editableByFieldMapping,
                fieldName: field.fieldName,
                fieldType: field.fieldType,
                fieldDataType: field.fieldDataType,
                optionsAvailable: field.optionsAvailable,
              },
            }) as CustomOption<NativeFieldExtra>,
        ),
    [accountAndProjectData, projectFieldsText, calculatedFieldsText],
  );

  const accountFieldAsOptions = useMemo(
    () =>
      (accountAndProjectData ?? [])
        .filter((data) => data?.parentFieldId === 'company')
        .map(
          (field) =>
            ({
              value: field.integrationFieldId,
              label: field.fieldLabel ?? field.fieldName,
              parent: companyFieldsText,
              extra: {
                isEditable: field.editableByFieldMapping,
                fieldName: field.fieldName,
                fieldType: field.fieldType,
                fieldDataType: field.fieldDataType,
                optionsAvailable: field.optionsAvailable,
              },
            }) as CustomOption<NativeFieldExtra>,
        ),
    [accountAndProjectData, companyFieldsText],
  );

  // ✅ Add user/contact field options
  const userFieldAsOptions = useMemo(
    () =>
      (accountAndProjectData ?? [])
        .filter((data) => data?.parentFieldId === 'user')
        .map(
          (field) =>
            ({
              value: field.integrationFieldId,
              label: field.fieldLabel ?? field.fieldName,
              parent: userFieldsText,
              extra: {
                isEditable: field.editableByFieldMapping,
                fieldName: field.fieldName,
                fieldType: field.fieldType,
                fieldDataType: field.fieldDataType,
                optionsAvailable: field.optionsAvailable,
              },
            }) as CustomOption<NativeFieldExtra>,
        ),
    [accountAndProjectData, userFieldsText],
  );

  // ✅ Dynamically build options based on nativeResourceType
  const nativeFieldAsOptions = useMemo(() => {
    if (fieldObject.nativeResourceType === 'user') {
      return userFieldAsOptions;
    } else if (fieldObject.nativeResourceType === 'company') {
      return accountFieldAsOptions;
    } else if (fieldObject.nativeResourceType === 'project') {
      return projectNativeFieldAsOptions.concat(accountFieldAsOptions);
    }

    // Fallback: show all
    return projectNativeFieldAsOptions
      .concat(accountFieldAsOptions)
      .concat(userFieldAsOptions);
  }, [
    fieldObject.nativeResourceType,
    projectNativeFieldAsOptions,
    accountFieldAsOptions,
    userFieldAsOptions,
  ]);

  const availableSyncOptions = useMemo(
    () => getValidSyncOptions(fieldMap),
    [fieldMap],
  );

  const { container, mainCell, syncCell, deleteButton } =
    fieldMappingVariants();

  return (
    <div className={container()}>
      <div className={mainCell()} style={{ flex: 1 }}>
        <PopoverSelect
          loading={isCreatingMapping}
          onChange={handleExternalFieldChange}
          value={fieldMap?.salesforceField}
          options={externalFields}
          type="salesforce"
          fieldObject={fieldObject}
        />
      </div>

      <div
        className={syncCell()}
        style={{
          width: '32px',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        {fieldMap?.salesforceField && (
          <div className="flex items-center justify-center w-full h-full">
            <Select
              isSearchable={false}
              styles={{
                ...SyncSelectStyles,
                control: (provided) => ({
                  ...provided,
                  boxShadow: 'none',
                  border: 'none',
                  minHeight: 'auto',
                  height: '16px',
                  width: '16px',
                  backgroundColor: 'transparent',
                }),
                valueContainer: (provided) => ({
                  ...provided,
                  padding: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }),
                singleValue: (provided) => ({
                  ...provided,
                  margin: 0,
                  position: 'static',
                  transform: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }),
              }}
              onChange={(val: any) => handleSyncOptionChange(val)}
              menuPortalTarget={document.body}
              menuPosition="fixed"
              formatOptionLabel={SyncOptionLabel}
              options={availableSyncOptions}
              isDisabled={availableSyncOptions.length <= 1}
              value={
                syncOptions.find(
                  (option) => option.value === fieldMap?.syncField,
                ) ??
                syncOptions.find(
                  (option) =>
                    option.value === SalesforceSyncTypes.SyncExternalToNative,
                )
              }
              defaultValue={syncOptions.find(
                (option) =>
                  option.value === SalesforceSyncTypes.SyncExternalToNative,
              )}
              components={{
                DropdownIndicator: () => null,
                IndicatorsContainer: () => null,
                IndicatorSeparator: () => null,
                SingleValue: SelectedSyncValue,
              }}
              isLoading={isCreatingMapping}
            />
          </div>
        )}
      </div>

      <div className={mainCell()} style={{ flex: 1 }}>
        {fieldMap?.salesforceField && (
          <PopoverSelect
            onVisibilityChange={setGetFields}
            onChange={handleNativeFieldChange}
            value={fieldMap?.rocketlaneField}
            options={nativeFieldAsOptions}
            loading={isLoading || isCreatingMapping}
            triggerLabel={
              <FormattedMessage id="settings.integrations.sf.resource_mappings.rocketlane.trigger" />
            }
            type="rocketlane"
            fieldObject={fieldObject}
          />
        )}
      </div>

      <div className={mainCell()} style={{ flex: 1, justifyContent: 'center' }}>
        <Popconfirm
          onConfirm={onRemove}
          subTitle={
            <FormattedMessage id="settings.integrations.sf.mapping.delete" />
          }
        >
          <div className={deleteButton()}>
            <FontAwesomeIcon icon={faCircleMinus} className="text-[12px]" />
          </div>
        </Popconfirm>
      </div>
    </div>
  );
};
